/**
 * Proposal inline-editing engine.
 *
 * A lightweight React context lets the SAME section renderers be used in two
 * modes without prop-drilling:
 *   - Viewer (edit mode ON): every prose field becomes click-to-edit.
 *   - PDF export / read-only: fields render as plain text.
 *
 * Renderers wrap each editable string in <EditableText path="body.opening"
 * value={...} render={renderText} />. The provider (the proposal viewer) gets
 * onFieldChange(path, value) and applies an immutable deep-set to the section
 * JSON, then autosaves.
 */

import React, { createContext, useContext, useEffect, useRef, useState } from 'react';

// Coerce any value to a safe string. AI-regenerated sections occasionally return
// arrays/objects where a string is expected; never let that crash editing.
function toStr(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (Array.isArray(v)) return v.map(toStr).filter(Boolean).join('\n');
  try { return JSON.stringify(v); } catch { return String(v); }
}

interface ProposalEditCtx {
  editing: boolean;
  onFieldChange?: (path: string, value: string) => void;
}

const Ctx = createContext<ProposalEditCtx>({ editing: false });

export const ProposalEditProvider = Ctx.Provider;
export function useProposalEdit(): ProposalEditCtx {
  return useContext(Ctx);
}

// Immutable deep-set for dot/bracket paths like "body.opening" or
// "keyDiscriminators.2.description". Returns a new object.
export function setByPath<T>(obj: T, path: string, value: unknown): T {
  const keys = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
  if (keys.length === 0) return obj;
  const clone: any = Array.isArray(obj) ? [...(obj as any)] : { ...(obj as any) };
  let cur = clone;
  for (let i = 0; i < keys.length - 1; i++) {
    const k = keys[i];
    const existing = cur[k];
    cur[k] = Array.isArray(existing) ? [...existing] : { ...(existing ?? {}) };
    cur = cur[k];
  }
  cur[keys[keys.length - 1]] = value;
  return clone;
}

interface EditableTextProps {
  /** Dot path of this field within the section JSON (e.g. "body.opening"). */
  path: string;
  /** Current string value. */
  value: string;
  /** How to render the value when NOT editing (e.g. citation highlighter). */
  render?: (v: string) => React.ReactNode;
  /** Render a single-line input instead of a growing textarea. */
  multiline?: boolean;
  className?: string;
  placeholder?: string;
}

/**
 * Click-to-edit text. Reads edit state from context. When editing is off (or no
 * change handler is provided) it renders plain/derived text, so the same
 * renderer is safe to use in static exports.
 */
export function EditableText({
  path,
  value,
  render,
  multiline = true,
  className = '',
  placeholder = 'Click to add text…',
}: EditableTextProps) {
  const { editing, onFieldChange } = useProposalEdit();
  const safeValue = toStr(value);
  const [active, setActive] = useState(false);
  const [draft, setDraft] = useState(safeValue);
  const taRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!active) setDraft(safeValue);
  }, [safeValue, active]);

  // Auto-size the textarea to its content.
  useEffect(() => {
    if (active && taRef.current) {
      const el = taRef.current;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
      el.focus();
      const len = el.value.length;
      el.setSelectionRange(len, len);
    }
  }, [active]);

  if (!editing || !onFieldChange) {
    return <>{render ? render(safeValue) : safeValue}</>;
  }

  const commit = () => {
    setActive(false);
    if (draft !== safeValue) onFieldChange(path, draft);
  };

  if (active) {
    return (
      <textarea
        ref={taRef}
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          const el = e.target as HTMLTextAreaElement;
          el.style.height = 'auto';
          el.style.height = `${el.scrollHeight}px`;
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setDraft(safeValue); setActive(false); }
          if (e.key === 'Enter' && !multiline && !e.shiftKey) { e.preventDefault(); commit(); }
        }}
        rows={1}
        className={`w-full resize-none rounded-md border border-blue-400 bg-bw-primary-tint/40 dark:bg-blue-900/20 px-2 py-1 text-inherit leading-relaxed focus:outline-none focus:ring-2 focus:ring-blue-400 ${className}`}
      />
    );
  }

  const isEmpty = safeValue.trim().length === 0;
  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => { setDraft(safeValue); setActive(true); }}
      onKeyDown={(e) => { if (e.key === 'Enter') { setDraft(safeValue); setActive(true); } }}
      title="Click to edit"
      className={`cursor-text rounded px-0.5 -mx-0.5 transition-colors hover:bg-fit-moderate-bg/70 dark:hover:bg-fit-moderate-bg0/10 ring-1 ring-transparent hover:ring-yellow-300/60 ${isEmpty ? 'italic text-bw-muted' : ''} ${className}`}
    >
      {isEmpty ? placeholder : (render ? render(safeValue) : safeValue)}
    </span>
  );
}
